import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { styled } from "@mui/material/styles";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Modal from "@mui/material/Modal";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import IconButton from "@mui/material/IconButton";
import { Check, X, Users, Info } from "lucide-react";
import { highlightText } from "components/common/utils";
import toast from "react-hot-toast";

import { getSubmissions } from "server/StartupAdmins CRUD/Requests";
import { GetSubmissionsOutput } from "server/StartupAdmins CRUD/OutputsTypes";
import { ApiResponse } from "server";
import { ExerciseStatus, SubmissionType } from "types";
import { timeAgo, exerciseTitle } from "components/common/utils";
import { useNavigate } from "react-router-dom";
import SubmissionDetailsModal from "./submissionDetails";
import AcceptSubmissionModal from "./AcceptSubmissionModal";
import RejectSubmissionModal from "./RejectSubmissionModal";
import AssignTeachersModal from "./AssignTeachersModal";
import {
  rejectExercise,
  updateCorrectorOrSupervisor,
  activateExercise,
} from "server/StartupAdmins CRUD/Requests";
import {
  RejectExerciseInput,
  UpdateCorrectorOrSupervisorInput,
} from "server/StartupAdmins CRUD/InputsTypes";

const CustomTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: theme.typography.fontWeightMedium,
  fontSize: theme.typography.pxToRem(14),
  minHeight: "48px",
  padding: "8px 16px",
  [theme.breakpoints.down("sm")]: {
    fontSize: theme.typography.pxToRem(12),
    padding: "6px 12px",
    minHeight: "40px",
  },
  "&.Mui-selected": {
    backgroundColor: "#E3B505",
    color: "black",
  },
}));

interface SubmissionListItemProps {
  submission: GetSubmissionsOutput;
  index: number;
  onValidate: (submissionId: string) => void;
  onReject: (submissionId: string) => void;
  onAssign: (submissionId: string) => void;
  onViewDetails: (submission: GetSubmissionsOutput) => void;
  searchQuery: string;
}

const SubmissionListItem: React.FC<SubmissionListItemProps> = ({
  submission,
  index,
  onValidate,
  onReject,
  onAssign,
  onViewDetails,
  searchQuery,
}) => {
  const [showActions, setShowActions] = React.useState(false);

  const isStandby = submission.status === ExerciseStatus.STANDBY;
  const isActivated = submission.isActivated;

  return (
    <div
      className="flex flex-col sm:grid sm:grid-cols-12 sm:gap-4 p-4 bg-white rounded-lg shadow-sm hover:bg-gray-50 relative group"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Mobile: Layout en colonne, Desktop: Layout en grille */}
      <div className="flex-1 mb-3 sm:mb-0 sm:col-span-5">
        <Typography variant="body1" className="font-bold text-sm sm:text-base">
          {index + 1}. {highlightText(exerciseTitle(submission), searchQuery)}
        </Typography>
        <Typography
          variant="body2"
          className="text-gray-600 text-xs sm:text-sm"
        >
          {submission.student?.schoolName ||
            submission.referenceTest?.schoolName ||
            "Établissement inconnu"}
        </Typography>
      </div>

      <div className="flex justify-between items-center sm:col-span-7">
        {/* Date */}
        <div className="sm:col-span-2">
          <Typography
            variant="body2"
            className="text-gray-500 text-xs sm:text-sm"
          >
            {timeAgo(new Date(submission.createdAt))}
          </Typography>
        </div>

        {/* Actions */}
        <div className="flex space-x-2 sm:col-span-5 sm:justify-end">
          {isStandby &&
            (isActivated ? (
              <IconButton
                onClick={() => onAssign(submission._id)}
                className="text-blue-600 hover:bg-blue-50"
                size="small"
                title="Assigner des professeurs"
                sx={{ width: 40, height: 40 }}
              >
                <Users size={18} />
              </IconButton>
            ) : (
              <>
                <IconButton
                  onClick={() => onValidate(submission._id)}
                  className="text-green-600 hover:bg-green-50"
                  size="small"
                  title="Valider"
                  sx={{ width: 40, height: 40 }}
                >
                  <Check size={18} />
                </IconButton>

                <IconButton
                  onClick={() => onReject(submission._id)}
                  className="text-red-600 hover:bg-red-50"
                  size="small"
                  title="Rejeter"
                  sx={{ width: 40, height: 40 }}
                >
                  <X size={18} />
                </IconButton>
              </>
            ))}

          <IconButton
            onClick={() => onViewDetails(submission)}
            className="text-gray-600 hover:bg-gray-100"
            size="small"
            title="Voir les détails"
            sx={{ width: 40, height: 40 }}
          >
            <Info size={18} />
          </IconButton>
        </div>
      </div>
    </div>
  );
};

export default function SubmissionTabs() {
  const [value, setValue] = React.useState(0);
  const [selectedType, setSelectedType] = React.useState("");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [submissions, setSubmissions] = React.useState<
    GetSubmissionsOutput[] | null
  >(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [openDetailsModal, setOpenDetailsModal] = React.useState(false);
  const [selectedSubmission, setSelectedSubmission] =
    React.useState<GetSubmissionsOutput | null>(null);
  const [openAcceptModal, setOpenAcceptModal] = React.useState(false);
  const [openRejectModal, setOpenRejectModal] = React.useState(false);
  const [openAssignModal, setOpenAssignModal] = React.useState(false);
  const [selectedSubmissionId, setSelectedSubmissionId] = React.useState<
    string | null
  >(null);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const fetchSubmissions = async () => {
    try {
      const response: ApiResponse<GetSubmissionsOutput[]> =
        await getSubmissions();
      if (response.status === 200 && Array.isArray(response.body)) {
        setError(null);
        setSubmissions(response.body);
      } else {
        setError("Erreur lors de la récupération des soumissions.");
      }
    } catch (err) {
      setError("Une erreur s'est produite lors de la connexion au serveur.");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchSubmissions();
  }, []);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleSubmissionTypeChange = (event: SelectChangeEvent<string>) => {
    setSelectedType(event.target.value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleAccept = async (submissionId: string) => {
    const promise = activateExercise(submissionId);
    toast.promise(promise, {
      loading: "Validation en cours...",
      success: (res) => {
        if (res.status === 200) {
          fetchSubmissions();
          return "Soumission validée avec succès !";
        } else {
          throw new Error(
            (res.body as { message: string }).message ||
              "Une erreur est survenue.",
          );
        }
      },
      error: (err) => err.message || "Impossible de valider la soumission.",
    });
  };

  const handleReject = async (submissionId: string, reason: string) => {
    const input: RejectExerciseInput = { observation: reason };
    const promise = rejectExercise(submissionId, input);

    toast.promise(promise, {
      loading: "Rejet en cours...",
      success: (res) => {
        if (res.status === 200) {
          fetchSubmissions();
          setOpenRejectModal(false);
          return "Soumission rejetée avec succès.";
        } else {
          throw new Error(
            (res.body as { message: string }).message ||
              "Une erreur est survenue lors du rejet.",
          );
        }
      },
      error: (err) => err.message || "Impossible de rejeter la soumission.",
    });
  };

  const handleAssign = async (
    submissionId: string,
    assignment: { teacher: string; controller: string },
  ) => {
    const input: UpdateCorrectorOrSupervisorInput = {
      correctorId: assignment.teacher,
      supervisorId: assignment.controller,
    };
    const promise = updateCorrectorOrSupervisor(submissionId, input);

    toast.promise(promise, {
      loading: "Assignation en cours...",
      success: (res) => {
        if (res.status === 200) {
          fetchSubmissions();
          setOpenAssignModal(false);
          return "Assignation réussie !";
        } else {
          throw new Error(
            (res.body as { message: string }).message ||
              "Une erreur est survenue lors de l'assignation.",
          );
        }
      },
      error: (err) => err.message || "Erreur lors de l'assignation.",
    });
  };

  const handleViewDetails = (submission: GetSubmissionsOutput) => {
    setSelectedSubmission(submission);
    setOpenDetailsModal(true);
  };

  const getFilteredSubmissions = () => {
    if (!submissions) return [];

    let filteredSubmissions = submissions;

    switch (value) {
      case 0:
        filteredSubmissions = filteredSubmissions.filter(
          (submission) => submission.status === ExerciseStatus.STANDBY,
        );
        break;
      case 1:
        filteredSubmissions = filteredSubmissions.filter(
          (submission) => submission.status === ExerciseStatus.PROCESSING,
        );
        break;
      case 2:
        filteredSubmissions = filteredSubmissions.filter(
          (submission) => submission.status === ExerciseStatus.ANSWERED,
        );
        break;
      case 3:
        filteredSubmissions = filteredSubmissions.filter(
          (submission) => submission.status === ExerciseStatus.REJECTED,
        );
        break;
    }

    if (selectedType) {
      filteredSubmissions = filteredSubmissions.filter(
        (submission) => submission.submissionType === selectedType,
      );
    }

    if (searchQuery) {
      filteredSubmissions = filteredSubmissions.filter((submission) =>
        exerciseTitle(submission)
          .toLowerCase()
          .includes(searchQuery.toLowerCase()),
      );
    }

    return filteredSubmissions.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          maxWidth: "full",
          margin: "auto",
          bgcolor: "background.paper",
          p: { xs: 1, sm: 2 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: "100%",
              overflow: "hidden",
              mb: 2,
            }}
          >
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="soumissions"
              textColor="inherit"
              TabIndicatorProps={{ style: { display: "none" } }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                "& .MuiTabs-scrollButtons": {
                  "&.Mui-disabled": {
                    opacity: 0.3,
                  },
                },
                "& .MuiTabs-scrollButtons.MuiTabs-scrollButtonsDesktop": {
                  display: { xs: "flex", sm: "flex" },
                },
              }}
            >
              <CustomTab label="En attente" />
              <CustomTab label="Assignées" />
              <CustomTab label="Terminées" />
              <CustomTab label="Rejetées" />
            </Tabs>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 1.5, sm: 2 },
            }}
          >
            <input
              type="text"
              placeholder="Rechercher..."
              className="w-full px-3 sm:px-4 py-2 focus:outline-none rounded-lg shadow-sm bg-gray-100 border border-gray-200 text-sm"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <FormControl
              variant="standard"
              sx={{
                minWidth: { xs: "100%", sm: 200 },
                "& .MuiInput-root": {
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                },
              }}
            >
              <Select
                value={selectedType}
                onChange={handleSubmissionTypeChange}
                displayEmpty
                sx={{
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  "& .MuiSelect-select": {
                    padding: { xs: "8px 12px", sm: "12px 16px" },
                  },
                }}
              >
                <MenuItem
                  value=""
                  sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                  Tous les types
                </MenuItem>
                <MenuItem
                  value="control"
                  sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                  Contrôle
                </MenuItem>
                <MenuItem
                  value="blocage"
                  sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                  Blocage
                </MenuItem>
                <MenuItem
                  value="unclear"
                  sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                  Non démarré
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <div className="space-y-2">
          {getFilteredSubmissions().length === 0 ? (
            <p className="text-gray-500 text-center text-sm">
              Aucune soumission trouvée.
            </p>
          ) : (
            getFilteredSubmissions().map((submission, index) => (
              <SubmissionListItem
                key={submission._id}
                submission={submission}
                index={index}
                onValidate={() => {
                  setSelectedSubmissionId(submission._id);
                  setOpenAcceptModal(true);
                }}
                onReject={() => {
                  setSelectedSubmissionId(submission._id);
                  setOpenRejectModal(true);
                }}
                onAssign={() => {
                  setSelectedSubmissionId(submission._id);
                  setOpenAssignModal(true);
                }}
                onViewDetails={handleViewDetails}
                searchQuery={searchQuery}
              />
            ))
          )}
        </div>
      </Box>

      <Box
        sx={{ display: "flex", justifyContent: "center", mt: 4, zIndex: 9999 }}
      >
        {/* Modals en dehors de la Box principale */}
        <AcceptSubmissionModal
          open={openAcceptModal}
          onClose={() => setOpenAcceptModal(false)}
          handleAccept={handleAccept}
          submissionId={selectedSubmissionId}
        />
        <RejectSubmissionModal
          open={openRejectModal}
          onClose={() => setOpenRejectModal(false)}
          handleReject={handleReject}
          submissionId={selectedSubmissionId}
        />
        <AssignTeachersModal
          open={openAssignModal}
          onClose={() => setOpenAssignModal(false)}
          handleAssign={handleAssign}
          submissionId={selectedSubmissionId}
        />

        {selectedSubmission && (
          <SubmissionDetailsModal
            open={openDetailsModal}
            onClose={() => {
              setOpenDetailsModal(false);
              setSelectedSubmission(null);
              fetchSubmissions();
            }}
            submission={selectedSubmission}
          />
        )}
      </Box>
    </>
  );
}
